import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Cargar carrito desde sessionStorage
  useEffect(() => {
    const storedCart = sessionStorage.getItem("cart");
    if (storedCart) {
      try {
        setItems(JSON.parse(storedCart));
      } catch {
        setItems({});
      }
    }
    setIsLoading(false);
  }, []);

  // Guardar en sessionStorage cuando cambie el carrito
  useEffect(() => {
    if (!isLoading) {
      sessionStorage.setItem("cart", JSON.stringify(items));
    }
  }, [items, isLoading]);

  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const key = product.codigo || product.id;
      const existing = prev[key];
      return {
        ...prev,
        [key]: {
          ...product,
          quantity: (existing?.quantity || 0) + quantity,
        },
      };
    });
  }, []);

  const removeItem = useCallback((productKey) => {
    setItems((prev) => {
      const newItems = { ...prev };
      delete newItems[productKey];
      return newItems;
    });
  }, []);

  const updateQuantity = useCallback((productKey, quantity) => {
    if (quantity <= 0) {
      removeItem(productKey);
      return;
    }
    setItems((prev) => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        quantity,
      },
    }));
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems({});
  }, []);

  const getItemCount = useCallback(() => {
    return Object.values(items).reduce((sum, item) => sum + (item.quantity || 0), 0);
  }, [items]);

  const getTotal = useCallback(() => {
    return Object.values(items).reduce(
      (sum, item) => sum + (item.precio || 0) * (item.quantity || 0),
      0
    );
  }, [items]);

  const value = {
    items,
    isLoading,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemCount,
    getTotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart debe usarse dentro de un CartProvider");
  }
  return context;
}

export default CartContext;
